import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ADMIN_API_URL, ORCHESTRATOR_API_URL } from './endpoints';
import { tokenStore, purgeLegacyTokenStorage } from '../utils/tokenStore';

// ── Shared Types ───────────────────────────────────────────────────
export interface User {
  user_id: string;
  id?: string;
  email: string;
  username: string;
  full_name?: string;
  role?: string;
  language: string;
  organisation_id?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  created_at?: string;
}

export interface Guideline {
  guideline_docs_id: string;
  title: string;
  description: string;
  image_url?: string;
  source_org: string;
  author: string;
  language: string;
  version_label: string;
  published_date: string;
  created_at: string;
  updated_at: string;
  chunk_count: number;
  embedded_count?: number;
  [key: string]: unknown;
}

export interface ChunkEmbeddingInfo {
  embedding_model: string;
  created_at: string;
  task_id?: string | null;
}

export interface GuidelineChunk {
  chunk_id: string;
  chunk_index: number;
  page_no: number;
  order_index: number;
  text: string;
  to_embed: boolean;
  task_id?: string | null;
  embed_status?: ChunkEmbeddingInfo | null;
  [key: string]: unknown;
}

export interface GuidelineDocDetail {
  guideline_docs_id: string;
  title: string;
  description: string;
  image_url?: string;
  source_org: string;
  author: string;
  version_label: string;
  published_date: string;
  url: string;
  language: string;
  jurisdiction: string;
  scope: string;
  created_at: string;
  updated_at: string;
  chunks: GuidelineChunk[];
  [key: string]: unknown;
}

export interface OrganisationSettings {
  [key: string]: unknown;
}

export interface Organisation {
  organisation_id: string;
  name: string;
  description?: string;
  domain?: string;
  contact_email?: string;
  contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  subscription_tier?: 'basic' | 'professional' | 'enterprise';
  is_active: boolean;
  max_users?: number;
  max_guidelines?: number;
  user_count?: number;
  guideline_count?: number;
  settings?: OrganisationSettings;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface OrganisationGuideline {
  organisation_guideline_id: string;
  organisation_id: string;
  guideline_docs_id: string;
  guideline_title?: string;
  guideline_description?: string;
  added_at: string;
  added_by?: string;
  is_active: boolean;
  notes?: string;
}

export interface PipelineRun {
  id: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface HealthResponse {
  status: string;
  [key: string]: unknown;
}

// ── Axios Instances ────────────────────────────────────────────────
const adminApi: AxiosInstance = axios.create({
  baseURL: ADMIN_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

const orchestratorApi: AxiosInstance = axios.create({
  baseURL: ORCHESTRATOR_API_URL,
  headers: { 'Content-Type': 'application/json' },
  // Sends/receives the httpOnly refresh-token cookie on /auth/* requests.
  withCredentials: true,
});

// ── Auth Interceptor ───────────────────────────────────────────────
adminApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

orchestratorApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors for both APIs
const handle401 = (error: any) => {
  if (error.response?.status === 401) {
    // Token has expired or is invalid - redirect to login to get a fresh token
    tokenStore.clear();
    purgeLegacyTokenStorage();
    const demoBase = process.env.REACT_APP_DEMO_FRONTEND_URL || '';
    if (demoBase) {
      const loginUrl = `${demoBase}/login?returnUrl=${encodeURIComponent(window.location.href)}`;
      console.log('Token expired (401), redirecting to login for refresh...');
      window.location.href = loginUrl;
    } else {
      // No external demo frontend configured — surface error
      console.warn('Auth 401 received but REACT_APP_DEMO_FRONTEND_URL is not set; skipping redirect.');
    }
  }
  return Promise.reject(error);
};

adminApi.interceptors.response.use(
  (response: AxiosResponse) => response,
  handle401,
);

orchestratorApi.interceptors.response.use(
  (response: AxiosResponse) => response,
  handle401,
);

// ── API Modules ────────────────────────────────────────────────────
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    orchestratorApi.post('/api/v1/auth/login', credentials),
  // Silently exchange the httpOnly refresh cookie for a fresh access token.
  // No refresh token is (or can be) read by this app (issue #6).
  refreshOrchestratorToken: () =>
    orchestratorApi.post<{ access_token: string }>('/api/v1/auth/refresh'),
  whoami: () => adminApi.get<User>('/admin/whoami'),
};

export interface SyncEmbeddingResult {
  chunk_id: string;
  task_id: string;
  status: 'synced' | 'pending' | 'failed' | 'error';
  message?: string;
}

export interface SyncEmbeddingsResponse {
  synced: number;
  pending: number;
  failed: number;
  errors: number;
  results: SyncEmbeddingResult[];
}

export const guidelinesApi = {
  list: (params?: { limit?: number; offset?: number; q?: string }) =>
    adminApi.get<PaginatedResponse<Guideline>>('/admin/guidelines', { params }),
  get: (id: string) => adminApi.get<GuidelineDocDetail>(`/admin/guidelines/${id}`),
  update: (id: string, data: Partial<Guideline>) =>
    adminApi.put<GuidelineDocDetail>(`/admin/guidelines/${id}`, data),
  delete: (id: string) =>
    adminApi.delete<{ deleted: boolean; guideline_docs_id: string; message: string }>(`/admin/guidelines/${id}`),
  assignToOrg: (guidelineId: string, data: { organisation_id: string; notes?: string }) =>
    adminApi.post<OrganisationGuideline>(`/admin/guidelines/${guidelineId}/organisations`, data),
  updateChunk: (guidelineId: string, chunkId: string, data: { to_embed: boolean }) =>
    adminApi.patch<Partial<GuidelineChunk>>(`/admin/guidelines/${guidelineId}/chunks/${chunkId}`, data),
  syncPendingEmbeddings: () =>
    adminApi.post<SyncEmbeddingsResponse>('/admin/guidelines/sync-pending-embeddings'),
  uploadImage: (file: File, filename: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const uri = `local://guidelines/images/${filename}`;
    return adminApi.post<{ uri: string; size: number; content_type: string | null }>(
      `/admin/guidelines/storage/upload?uri=${encodeURIComponent(uri)}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  },
};

// Pipeline API interfaces
export interface PipelineUploadJsonResponse {
  ocr_json_path: string;
}

export interface PdfUploadResponse {
  file_uri: string;
  filename: string;
  size_bytes: number;
  message: string;
}

export interface PipelineSubmitResponse {
  task_id: string;
}

export interface PipelineTaskStatus {
  task_id: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILURE' | 'REVOKED';
  progress?: string;
  result?: {
    sections: any[];
    chunks: any[];
    embeddings: any[];
  };
  error?: string;
}

// Routed through admin-server (which permits the admin origin via CORS) rather
// than calling the static inference API directly from the browser, which the
// browser blocks with a CORS error. admin-server proxies to static inference.
export const guidelinePipelineApi = {
  uploadJson: (file: File, guidelineName: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('guideline_name', guidelineName);
    return adminApi.post<PipelineUploadJsonResponse>(
      '/admin/guidelines/pipeline/upload-json',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  },

  uploadPdf: (file: File, guidelineName: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('guideline_name', guidelineName);
    return adminApi.post<PdfUploadResponse>(
      '/admin/guidelines/pipeline/upload-pdf',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  },

  // Submit pipeline processing task
  submitPipeline: (payload: {
    guideline_name: string;
    ocr_json_path?: string;
    file_uri?: string;
    skip_summarisation?: boolean;
    upload_to_backend: boolean;
    doc_payload?: Record<string, unknown>;
  }) => {
    return adminApi.post<PipelineSubmitResponse>(
      '/admin/guidelines/pipeline',
      payload
    );
  },

  // Poll task status
  getTaskStatus: (taskId: string) => {
    return adminApi.get<PipelineTaskStatus>(
      `/admin/guidelines/pipeline/${taskId}`
    );
  },
};

export const pipelineStatusApi = {
  getStatus: () => adminApi.get('/admin/pipeline/status'),
};

export const healthApi = {
  check: () => adminApi.get<HealthResponse>('/health'),
};

export const organisationsApi = {
  list: (params?: { limit?: number; offset?: number; q?: string }) =>
    adminApi.get<PaginatedResponse<Organisation>>('/admin/organisations', { params }),
  get: (id: string) => adminApi.get<Organisation>(`/admin/organisations/${id}`),
  create: (data: Partial<Organisation>) =>
    adminApi.post<Organisation>('/admin/organisations', data),
  update: (id: string, data: Partial<Organisation>) =>
    adminApi.put<Organisation>(`/admin/organisations/${id}`, data),
  delete: (id: string) =>
    adminApi.delete<{ deleted: boolean; organisation_id: string; message: string }>(`/admin/organisations/${id}`),
  
  // Organisation guidelines
  listGuidelines: (organisationId: string, params?: { limit?: number; offset?: number }) =>
    adminApi.get<PaginatedResponse<OrganisationGuideline & { guideline?: Guideline }>>(
      `/admin/organisations/${organisationId}/guidelines`,
      { params }
    ),
  addGuideline: (organisationId: string, data: {
    guideline_docs_id: string;
    guideline_title: string;
    guideline_description?: string;
    notes?: string;
  }) =>
    adminApi.post<OrganisationGuideline>(
      `/admin/organisations/${organisationId}/guidelines`,
      data
    ),
  updateGuideline: (organisationId: string, guidelineId: string, data: {
    notes?: string;
    is_active?: boolean;
  }) =>
    adminApi.put<OrganisationGuideline>(
      `/admin/organisations/${organisationId}/guidelines/${guidelineId}`,
      data
    ),
  removeGuideline: (organisationId: string, guidelineId: string) =>
    adminApi.delete<{ deleted: boolean; message: string }>(
      `/admin/organisations/${organisationId}/guidelines/${guidelineId}`
    ),
};

export const usersApi = {
  list: (params?: { organisation_id?: string; skip?: number; limit?: number }) =>
    orchestratorApi.get<User[]>('/api/v1/users', { params }),
  create: (data: {
    email: string;
    username: string;
    password: string;
    role: string;
    language: string;
    full_name?: string;
    organisation_id?: string;
  }) => orchestratorApi.post<User>('/api/v1/users', data),
  update: (userId: string, data: { full_name?: string; role?: string; language?: string }) =>
    orchestratorApi.put<User>(`/api/v1/users/${userId}`, data),
  delete: (userId: string) =>
    orchestratorApi.delete<{ message: string }>(`/api/v1/users/${userId}`),
};

export { adminApi, orchestratorApi };
